<template>
  <v-container>
       <CRUDCriteria
        :showSearch="false"
        :showReset="true"
      >
        <template v-slot:criteriaRows>
          <v-row>
            <v-col cols="6">
              <v-text-field
                id="sourceWildCard"
                label="Wildcard search(Bank Name, Account number, Alt Key, Group Code or Bank Code)"
                 v-model="criteriaState.wildcard"
                @input="wildcardSearchDelayed"
                autocomplete="off"
              ></v-text-field>
            </v-col>
          </v-row>
        </template>
      </CRUDCriteria>
    <CRUDList
      :canAdd="canAddToSharedList"
      :canEdit="true"
      :canSave="canEditSharedList"
      :showSelect="true"
      :headers="headers"
      :mergeCriteria="false"
      :defaultSortBy="sortBy"
      :baseCrudKey="baseCrudKey"
      :selectedItems="selectedItems"
      :createNewItem="newItemDialog"
      toolbarTitle="Banks"
      @loadCRUDList="handleLoadCRUDList"
      @upsertItem="upsrtBank"
      @emitSelectedItem="emitSelectedItem"
      :loadingData="gridLoading.flag"
      :loadingText="gridLoading.text"
    >
      <template v-slot:[`additionalTools`]="{}">
        <v-checkbox
          id="showHiddenBanks"
          class="show-hidden-banks-checkbox"
          label="Show Hidden Banks"
          v-model="criteriaState.showHiddenBanks"
          @change="handleLoadCRUDList"
          color="primary"
          hide-details
          dense
          style="margin-top: 0; padding-top: 0;"
        >
        </v-checkbox>
      </template>
      <template v-slot:[`additionalToolsAtRight`]="{}">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              id="sharedBankListRenumberLinesBtn"
              class="shared-bank-list-renumber-lines-btn"
              color="primary"
              fab icon
              :disabled="(selectedItems.length) !== 1"
              @click="renumberLines"
            >
              <v-icon large>
                {{ icons.mdiCounter }}
              </v-icon>
            </v-btn>
          </template>
          <span>Renumber Lines</span>
        </v-tooltip>
      </template>
      <template v-slot:[`editedItem`]="{}">
        <BankEditedItem
          :baseCrudKey="baseCrudKey"
        />
      </template>
      <template v-slot:[`additionalActions`]="{}">
        <span v-if="!isNewItem() && canDeleteFromSharedList">
          <v-btn :class="`input-text-size`"
            text color="blue darken-1"
            @click="deleteBank"
            >Delete</v-btn>
        </span>
      </template>
    </CRUDList>
  </v-container>
</template>

<script>
import {
  mapGetters,
  mapActions,
  mapMutations,
  mapState,
} from 'vuex';
import { mdiCounter } from '@mdi/js';
import CRUDList from '../../common/base/crud/CRUDList.vue';
import BankEditedItem from './BankEditedItem.vue';
import { checkStringEmptiness, defaultGridLoading, manageGridLoading } from '../../../util/shared/vue-global';
import SharedListAccessControlMixin from '../SharedListAccessControl.mixin';
import CRUDCriteria from '../../common/base/crud/CRUDCriteria.vue';

const getHeaders = () => {
  const headers = [
    { text: 'Bank Name', value: 'bankName' },
    { text: 'Account Number', value: 'accountNumber' },
    { text: 'Routing Number', value: 'routingNumber' },
    { text: 'Alt Key', value: 'altKeyBase' },
    // for now added line in the headers so that can easily check if lines are renumbered or not
    { text: 'Line', value: 'line' },
    { text: 'Group Code', value: 'groupCode' },
    { text: 'Bank Code', value: 'bankCode' },
    // { text: 'Bank Address', value: 'bankAddress' },
    { text: 'Hide on Reports', value: 'hideOnReports', formatter: 'boolToStr' },
  ];
  return headers;
};

export default {
  name: 'BankList',
  components: {
    CRUDList,
    BankEditedItem,
    CRUDCriteria,
  },
  mixins: [SharedListAccessControlMixin],
  data: () => ({
    headers: [],
    baseCrudKey: 'sharedBank',
    collateralBankCrudKey: 'collateralBank',
    gridLoading: defaultGridLoading(),
    delayTimerId: undefined,

    sortBy: [
      {
        column: 'bankName',
        direction: 'asc',
      },
    ],
    icons: {
      mdiCounter,
    },
  }),
  created() {
    this.headers = getHeaders(this.canDelete);
    this.loadCollateralList();
  },
  computed: {
    ...mapGetters('base/crud', [
      'criteria',
      'index',
      'item',
      'items',
    ]),
    ...mapState({
      storedSelectedItems: (state) => state.shared.bank.selectedItems,
    }),
    selectedItems: {
      get() {
        return this.storedSelectedItems;
      },
      set(value) {
        this.setSelectedItems(value);
      },
    },
    criteriaState: {
      get() {
        return this.criteria(this.baseCrudKey);
      },
      set(value) {
        this.setCriteria([this.baseCrudKey, value]);
      },
    },
    bankGroupCodesUsingInitials() {
      const collateralBankObjs = this.items(this.collateralBankCrudKey) || [];
      const updatedObj = collateralBankObjs.map((c) => c.bankIntials).filter(Boolean);
      return updatedObj;
    },
  },
  methods: {
    manageGridLoading,
    ...mapActions(['flashInfo', 'flashSuccess']),
    ...mapActions('shared/bank', [
      'loadBanks',
      'loadBankWithCollateralList',
      'upsertBank',
      'deleteSharedBank',
    ]),
    ...mapMutations('base/crud', [
      'setCriteria',
    ]),
    ...mapMutations('shared/bank', [
      'setSelectedItems',
    ]),
    ...mapActions('common/renumberLines', [
      'cmnRenumberLines',
    ]),
    ...mapActions('glDaily/glCollateralBankTransaction', [
      'loadGLCollateralBankTrans',
    ]),
    wildcardSearchDelayed() {
      // cancel pending call
      clearTimeout(this.delayTimerId);
      const { baseCrudKey } = this;
      // delay new call
      this.delayTimerId = setTimeout(async () => {
        await this.loadBankWithCollateralList({
          criteria: { ...this.criteriaState, showHiddenReportBanks: true }, baseCrudKey,
        });
      }, 250);
    },
    handleLoadCRUDList() {
      const { baseCrudKey } = this;
      const criteria = { ...this.criteria(baseCrudKey), showHiddenReportBanks: true };
      manageGridLoading(this, true, 'Searching...');
      this.loadBankWithCollateralList({
        criteria,
        baseCrudKey,
      }).then(() => {
        this.setDefaultSelectedItems();
      }).finally(() => {
        manageGridLoading(this, false);
      });
    },
    isNewItem() {
      return this.index(this.baseCrudKey) === -1;
    },
    async upsrtBank(item) {
      const { baseCrudKey } = this;
      const updatedItem = { ...item };
      if (!this.isNewItem()) {
        if (!window.confirm('This will affect to all associated Transactions, are you sure!')) {
          return false;
        }
      }
      if (updatedItem.bankCollateralRecord) {
        delete updatedItem.bankCollateralRecord;
      }
      if (updatedItem.crudRowCssAppend) {
        delete updatedItem.crudRowCssAppend;
      }
      if (checkStringEmptiness(updatedItem.line)) {
        updatedItem.line = null;
      }
      if (checkStringEmptiness(updatedItem.isBaseAmount)) {
        updatedItem.isBaseAmount = null;
      }
      await this.upsertBank({ item: updatedItem, baseCrudKey });
      updatedItem.isBaseAmount = null;
      this.setDefaultSelectedItems();
      return true;
    },
    setDefaultSelectedItems() {
      this.$nextTick(() => {
        this.selectedItems = [];
      });
    },
    emitSelectedItem(item) {
      this.selectedItems = item;
    },
    newItemDialog() {
      let newItem = {};
      newItem = {
        bankCollateralRecord: {
          details: [],
        },
      };
      return newItem;
    },
    async renumberLines() {
      if (window.confirm('Are you sure you want to renumber lines?')) {
        if (this.selectedItems[0].line !== null) {
          await this.cmnRenumberLines({
            criteria: {
              line: this.selectedItems[0].line,
            },
            baseCrudKey: this.baseCrudKey,
          });
          this.handleLoadCRUDList();
          this.flashSuccess('All the line numbers are now successfully renumbered!');
        } else {
          this.setDefaultSelectedItems();
          this.flashInfo('The selected transaction does not contain line number!');
        }
      }
      this.setDefaultSelectedItems();
    },
    loadCollateralList() {
      this.loadGLCollateralBankTrans({ criteria: {}, baseCrudKey: this.collateralBankCrudKey });
    },
    async deleteBank() {
      const itemVal = this.item(this.baseCrudKey);
      if (window.confirm(`Are you sure you want to delete this Bank ${itemVal.bankName}?`)) {
        const { baseCrudKey } = this;
        const deleteBankObj = { _id: itemVal._id };
        await this.deleteSharedBank({ item: deleteBankObj, baseCrudKey }).then(() => {
          this.setDefaultSelectedItems();
        }).catch((e) => {
          console.warn(e);
          this.setDefaultSelectedItems();
        });
      }
    },
  },
};

</script>

<style lang="scss">
.hidden-bank-highlight {
  background-color: #bbdefb !important; // light blue highlight for hidden banks
}
</style>
